import type { RecipeFromVideoRequest } from '@recipes/contracts';
import { youtubeId } from '@recipes/domain';
import { Alert, Button, Input, Textarea } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { loadGenerationRequest, storeGenerationRequest } from '@/features/ai/generation-storage';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useConfig, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';

/** The form of a failed or edited generation (?restore=<id>), else empty. */
function restored(restoreId: string | null): RecipeFromVideoRequest | null {
  const stored = restoreId ? loadGenerationRequest(restoreId) : null;
  return stored?.kind === 'video' ? stored.body : null;
}

/** Recipe from a YouTube video: the server's model (Gemini) watches it and writes a draft. */
export function VideoGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const aiEnabled = useFeature('ai');
  const consented = useAiConsent();
  const { data: config } = useConfig();
  const videoAvailable = config?.ai.video ?? false;

  const [initial] = useState(() => restored(params.get('restore')));
  const [url, setUrl] = useState(initial?.url ?? params.get('url') ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [touched, setTouched] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: RecipeFromVideoRequest) => api.ai.recipeFromVideo(body, crypto.randomUUID()),
    onSuccess: (generation, body) => {
      storeGenerationRequest(generation.id, { kind: 'video', body });
      navigate(`/add/ai/${generation.id}`);
    },
  });

  const linkValid = youtubeId(url) !== null;
  const canSubmit = aiEnabled && videoAvailable && consented && linkValid;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('ai.video.title')}
        description={t('ai.video.description')}
        backTo="/add"
      />
      {!aiEnabled && <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>}
      {aiEnabled && config && !videoAvailable && (
        <Alert variant="warning">{t('ai.video.unavailable')}</Alert>
      )}
      {aiEnabled && !consented && <AiConsentNotice />}
      <form
        noValidate
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (canSubmit)
            mutation.mutate({
              url: url.trim(),
              description: description.trim() || undefined,
            });
        }}
      >
        <FormField
          label={t('ai.video.urlField')}
          hint={t('ai.video.urlHint')}
          error={touched && url.trim() && !linkValid ? t('ai.video.urlInvalid') : undefined}
          required
        >
          <Input
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://www.youtube.com/watch?v=…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </FormField>

        <FormField
          label={t('ai.video.descriptionField')}
          hint={t('ai.video.descriptionHint')}
          optionalText={t('common.optional')}
        >
          <Textarea
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>

        <Alert variant="info" title={t('ai.video.noticeTitle')}>
          {t('ai.video.notice')}
        </Alert>

        {mutation.error && (
          <Alert variant="destructive" title={t('ai.failed')}>
            {describeError(mutation.error)}
          </Alert>
        )}

        <Button type="submit" size="lg" loading={mutation.isPending} disabled={!canSubmit}>
          {t('ai.video.submit')}
        </Button>
      </form>
    </PageContainer>
  );
}
